"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { LeadFormModalProps } from "../../../app/utilities/interfaces";
import { submitLead } from "../../../app/utilities/apis";
import { PRODUCTION } from "@/app/utilities/constants";
import Link from "next/link";
import { usePathname } from "next/navigation";
import ImageWrapper from "../ImageWrapper";
import LeadForm from '@/component/ui/Lead/LeadForm';
import LeadOtpStep from '@/component/ui/Lead/LeadOtpStep';
import LeadDealerStep from '@/component/ui/Lead/LeadDealerStep';
import ThanksPopup from '@/component/ThanksPopup';
import { hasSubmittedLead, storeLeadSubmission } from '@/utils/leadTracking';
import AlreadySubmitted from '@/component/ui/AlreadySubmitted';
import { MODAL_TRACKING } from "@/lib/constants/tracking";
import { useLeadFormConfig } from "@/utils/hooks/useLeadFormConfig";
import { acquireModalSession, releaseModalSession } from "@/lib/client/modalSession";
import { THANKS_POPUP_SESSION_ID } from "@/app/utilities/constants";

const LeadFormModal: React.FC<LeadFormModalProps> = ({
    showModal,
    carId,
    variantId,
    onClose,
    onSubmit,
    onError,
    carName,
    is_upcoming = false,
    is_support = false,
    formHeading = '',
    LeadPopupSubmitTracking,
    leadsumbittracking,
    leadSource,
}) => {
    const [isMounted, setIsMounted] = useState(false);
    const [showThanks, setShowThanks] = useState(false);
    const [lastLeadData, setLastLeadData] = useState<any>(null);
    const [otpState, setOtpState] = useState<{ leadId: string; phone: string } | null>(null);
    const [dealerLeadId, setDealerLeadId] = useState<string | null>(null);
    const lastLeadPayloadRef = useRef<Record<string, unknown> | null>(null);
    const { emailRequired, dealerSelectionEnabled, loading: leadConfigLoading } = useLeadFormConfig(carId ? String(carId) : null);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        return () => releaseModalSession(THANKS_POPUP_SESSION_ID);
    }, []);

    const pathname = usePathname();

    const sendGTMEvent = (record: object) => {
        if ((window as any)?.gtag) {
            (window as any).gtag("event", "conversion", {
                'send_to': `${process.env.NEXT_PUBLIC_GTM_MARKETING_ADS_CONVERSION_ID}`,
            });
        }
    };

    const openThanksPopup = useCallback((leadData: Record<string, unknown>) => {
        if (typeof window !== "undefined") {
            (window as any).__LAST_LEAD_DATA__ = leadData;
            if (carId) {
                window.dispatchEvent(
                    new CustomEvent("lead-submitted", {
                        detail: { carId, carName: carName || "" },
                    }),
                );
            }
        }
        setLastLeadData(leadData);
        acquireModalSession(THANKS_POPUP_SESSION_ID);
        setShowThanks(true);
    }, [carId, carName]);

    const closeThanksPopup = useCallback(() => {
        setShowThanks(false);
        releaseModalSession(THANKS_POPUP_SESSION_ID);
    }, []);

    const closeAlreadySubmitted = () => {
        onClose();
    };

    const completeLeadSuccess = () => {
        const payload = lastLeadPayloadRef.current ?? {};
        const leadData = {
            ...payload,
            modelName: (payload as { modelName?: string }).modelName ?? carName,
        };
        openThanksPopup(leadData);
        onSubmit();
    };

    const handleLeadConfirmed = (leadId?: string) => {
        if (process.env.NEXT_PUBLIC_ENV === PRODUCTION) {
            const urlParams = new URLSearchParams(window.location.search);
            const gclid = urlParams.get('gclid');
            if (gclid && gclid.length > 0) {
                sendGTMEvent({
                    remark: `Lead submitted from  ${pathname}`,
                    date: new Date().toISOString(),
                });
            }
        }

        if (carId) {
            try { storeLeadSubmission(String(carId), carName || '', 'GORP', String(variantId)); } catch { }
        }
        setOtpState(null);
        if (dealerSelectionEnabled && leadId) {
            setDealerLeadId(leadId);
            return;
        }
        completeLeadSuccess();
    };

    const finishDealerStep = () => {
        setDealerLeadId(null);
        completeLeadSuccess();
    };

    if (!isMounted) return null;

    const thanksModel = carId
        ? {
            _id: carId,
            status: is_upcoming ? "upcoming" : undefined,
            name: carName,
        }
        : undefined;

    const checkLeadSubmitted = showModal && !dealerLeadId && carId
        ? hasSubmittedLead(String(carId), 'GORP', variantId)
        : false;

    const modalContent = checkLeadSubmitted ? (
        <AlreadySubmitted onClose={closeAlreadySubmitted} />
    ) : (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-100">
            <div className="bg-white rounded-[24px] w-[328px] md:w-[448px] max-w-full flex">

                <div className="w-full p-4 md:p-6 relative">
                    <div className="flex justify-between items-center  mb-4 pt-4">

                        <p className="text-[20px] leading-[36px] font-medium tracking-[-0.75px] font-roboto text-gray-800 relative line-clamp-2">
                            {formHeading ? (
                                formHeading
                            ) : (
                                <>
                                    {is_support ? 'Get Support for ' : is_upcoming ? 'Get Launch Updates on' : 'Get Special Offers on '} {" "}
                                    <span className="font-semibold tracking-[-0.75px] text-[#008C8B]">
                                        {carName}
                                    </span>
                                </>
                            )}
                        </p>
                        <button onClick={dealerLeadId ? finishDealerStep : onClose} className={`top-[16px]  right-[24] absolute cursor-pointer ${MODAL_TRACKING.MODAL_CLOSE}`}>
                            <ImageWrapper
                                src={'/popup-close.svg'}
                                alt="close Icon"
                                title="close Icon"
                                width={18}
                                height={18}
                                cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}
                            />
                        </button>
                    </div>


                    {dealerLeadId ? (
                        <LeadDealerStep leadId={dealerLeadId} carName={carName} onDone={finishDealerStep} />
                    ) : otpState ? (
                        <LeadOtpStep
                            leadId={otpState.leadId}
                            phone={otpState.phone}
                            onVerified={() => handleLeadConfirmed(otpState.leadId)}
                            onCancel={() => setOtpState(null)}
                        />
                    ) : (
                        <LeadForm
                            submitLabel="Submit"
                            onSubmit={async (form) => {
                                if (carId && hasSubmittedLead(String(carId), 'GORP', String(variantId))) {
                                    try { storeLeadSubmission(String(carId), carName || '', 'GORP', String(variantId)); } catch { }
                                    return false;
                                }

                                try {
                                    const { buildLeadPayload } = await import('@/app/utilities/locationUtils');
                                    const payload = buildLeadPayload(form, { carId, variantId, leadSource });
                                    lastLeadPayloadRef.current = payload;
                                    const leadSubmitResponse = await submitLead(payload);
                                    if (leadSubmitResponse.status === "otp_required") {
                                        setOtpState({ leadId: leadSubmitResponse.data.lead_id, phone: leadSubmitResponse.data.phone });
                                        return;
                                    }
                                    if (leadSubmitResponse.status !== "success") {
                                        onError("Failed to submit your information. Please try again.");
                                        return false;
                                    }

                                    handleLeadConfirmed(leadSubmitResponse.data?._id);
                                    return true;
                                } catch (err) {
                                    console.error("Failed to submit lead", err);
                                    onError("Failed to submit your information. Please try again.");
                                    return false;
                                }
                            }}
                            onError={(msg) => onError(msg)}
                            LeadPopupSubmitTracking={LeadPopupSubmitTracking}
                            leadsumbittracking={leadsumbittracking}
                            showEmail={emailRequired}
                            disabled={leadConfigLoading}
                        />
                    )}

                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(
        <>
            {showModal ? modalContent : null}
            <ThanksPopup
                isOpen={showThanks}
                onClose={closeThanksPopup}
                modelId={carId ? String(carId) : null}
                leadData={lastLeadData}
                model={thanksModel}
            />
        </>,
        document.body,
    );
};

export default LeadFormModal;
